/**
 * UserAvatar Component
 * 
 * A standardized avatar component for the entire HRMS system.
 * 
 * Avatar Rules:
 * 1. If user has an uploaded profile image → display the image
 * 2. If no image → display ONLY the first TWO letters of the English name
 * 3. Arabic initials must NEVER be displayed
 * 4. Fallback is strictly English initials or a safe default "US"
 * 
 * Styling follows EmployeeTable page design (size, shape, colors, typography)
 */

import React, { useState, useMemo } from 'react';

/**
 * Checks if a character is Arabic
 * @param {string} char - Single character to check
 * @returns {boolean}
 */
const isArabicChar = (char) => {
  if (!char) return false;
  const code = char.charCodeAt(0);
  // Arabic Unicode range: 0x0600-0x06FF (Arabic), 0x0750-0x077F (Arabic Supplement)
  // 0xFB50-0xFDFF (Arabic Presentation Forms-A), 0xFE70-0xFEFF (Arabic Presentation Forms-B)
  return (code >= 0x0600 && code <= 0x06FF) ||
         (code >= 0x0750 && code <= 0x077F) ||
         (code >= 0xFB50 && code <= 0xFDFF) ||
         (code >= 0xFE70 && code <= 0xFEFF);
};

/**
 * Checks if a string contains Arabic characters
 * @param {string} str - String to check
 * @returns {boolean}
 */
const containsArabic = (str) => {
  if (!str) return false;
  for (let i = 0; i < str.length; i++) {
    if (isArabicChar(str[i])) return true;
  }
  return false;
};

/**
 * Extracts English initials from a name string.
 * NEVER returns Arabic initials - always falls back to "US" if no English name is found.
 * 
 * @param {string} name - The name to extract initials from (prefer English name)
 * @param {string} englishName - Optional explicit English name field
 * @param {string} fallback - Fallback initials if no valid English name (default: "US")
 * @returns {string} Two uppercase English letters
 */
export const getEnglishInitials = (name, englishName = null, fallback = "US") => {
  // Priority: Use explicit English name if provided
  const nameToUse = englishName || name;
  
  if (!nameToUse || typeof nameToUse !== 'string') {
    return fallback;
  }

  const trimmedName = nameToUse.trim();
  
  // If the name contains Arabic characters, reject it and use fallback
  if (containsArabic(trimmedName)) {
    return fallback;
  }

  // Split by spaces and filter empty strings
  const words = trimmedName.split(/\s+/).filter(word => word.length > 0);

  if (words.length === 0) {
    return fallback;
  }

  // Get first letter of first word
  const firstLetter = words[0].charAt(0).toUpperCase();
  
  // Validate first letter is English (A-Z)
  if (!/[A-Z]/i.test(firstLetter)) {
    return fallback;
  }

  if (words.length === 1) {
    // Single word: use first two characters if available
    const secondChar = words[0].length > 1 ? words[0].charAt(1).toUpperCase() : '';
    if (secondChar && /[A-Z]/i.test(secondChar)) {
      return (firstLetter + secondChar).toUpperCase();
    }
    return firstLetter + firstLetter; // Repeat first letter if second is invalid
  }

  // Multiple words: use first letter of first and last word
  const lastWord = words[words.length - 1];
  const lastLetter = lastWord.charAt(0).toUpperCase();
  
  if (/[A-Z]/i.test(lastLetter)) {
    return (firstLetter + lastLetter).toUpperCase();
  }
  
  return (firstLetter + firstLetter).toUpperCase();
};

/**
 * Generates a consistent color based on a name for avatar backgrounds
 * @param {string} name - Name to generate color from
 * @returns {string} Color string
 */
export const getAvatarColor = (name) => {
  const colors = [
    { gradient: 'linear-gradient(135deg, #2e7cba 0%, #22354d 100%)' }, // Blue
    { gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' }, // Bright Blue
    { gradient: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' }, // Purple
    { gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }, // Pink
    { gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }, // Green
    { gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }, // Orange
    { gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }, // Cyan
    { gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }, // Violet
  ];
  
  if (!name) return colors[0].gradient;
  
  // Generate consistent index based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index].gradient;
};

/**
 * Avatar size presets matching EmployeeTable styling
 */
export const AVATAR_SIZES = {
  xs: {
    width: '2rem',
    height: '2rem',
    fontSize: '0.75rem',
    borderRadius: '50%',
  },
  sm: {
    width: '2.25rem',
    height: '2.25rem',
    fontSize: '0.875rem',
    borderRadius: '50%',
  },
  md: {
    width: '2.5rem',
    height: '2.5rem',
    fontSize: '1rem',
    borderRadius: '50%',
  },
  lg: {
    width: '3rem',
    height: '3rem',
    fontSize: '1.125rem',
    borderRadius: '50%',
  },
  xl: {
    width: '3.5rem',
    height: '3.5rem',
    fontSize: '1.25rem',
    borderRadius: '50%',
  },
  xxl: {
    width: '4rem',
    height: '4rem',
    fontSize: '1.5rem',
    borderRadius: '50%',
  },
  // Card style avatars (rounded corners instead of circle)
  'card-sm': {
    width: '48px',
    height: '48px',
    fontSize: '1rem',
    borderRadius: '12px',
  },
  'card-md': {
    width: '64px',
    height: '64px',
    fontSize: '1.25rem',
    borderRadius: '16px',
  },
  'card-lg': {
    width: '80px',
    height: '80px',
    fontSize: '1.5rem',
    borderRadius: '20px',
  },
};

/**
 * UserAvatar Component
 * 
 * @param {Object} props
 * @param {string} props.name - Display name (may be Arabic or English)
 * @param {string} props.englishName - English name for initials (preferred)
 * @param {string} props.imageUrl - Profile image URL
 * @param {string} props.size - Size preset: 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'card-sm', 'card-md', 'card-lg'
 * @param {boolean} props.isDarkMode - Theme mode
 * @param {Object} props.style - Additional custom styles
 * @param {string} props.className - Additional CSS class
 * @param {string} props.alt - Alt text for image
 * @param {function} props.onClick - Click handler
 */
const UserAvatar = ({
  name = '',
  englishName = null,
  imageUrl = null,
  size = 'md',
  isDarkMode = false,
  style = {},
  className = '',
  alt = '',
  onClick = null,
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Get initials from English name only
  const initials = useMemo(() => {
    return getEnglishInitials(name, englishName, 'US');
  }, [name, englishName]);
  
  // Get consistent avatar color
  const avatarGradient = useMemo(() => {
    return getAvatarColor(englishName || name);
  }, [name, englishName]);
  
  // Get size styles
  const sizeStyles = AVATAR_SIZES[size] || AVATAR_SIZES.md;
  
  // Check if we have a valid image to show
  const hasValidImage = imageUrl && !imageError;
  
  // Base styles matching EmployeeTable userAvatar
  const baseStyles = {
    width: sizeStyles.width,
    height: sizeStyles.height,
    borderRadius: sizeStyles.borderRadius,
    background: avatarGradient,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: sizeStyles.fontSize,
    fontWeight: '700',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    position: 'relative',
    border: '0.7px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    flexShrink: 0,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    userSelect: 'none',
    ...style,
  };

  const imageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 'inherit',
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div
      style={baseStyles}
      className={className}
      onClick={onClick}
      title={alt || name}
    >
      {hasValidImage ? (
        <img
          src={imageUrl}
          alt={alt || name || 'User avatar'}
          style={imageStyles}
          onError={handleImageError}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

export default UserAvatar;
